import { useEffect, useState, useRef } from 'react';
import './CrossPort.css';

function CrossPort() {
  const [scriptError, setScriptError] = useState(null);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const widgetRef = useRef(null);
  const cleanedRef = useRef(false);

  useEffect(() => {
    let widgetInstance = null; // hold widget reference
    let readyTimeout = null;

    const loadDeBridgeScript = (retryCount = 0, maxRetries = 3) => {
      return new Promise((resolve, reject) => {
        if (window.deBridge) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://app.debridge.finance/assets/scripts/widget.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          if (retryCount < maxRetries) {
            console.warn(`Retrying deBridge script load (${retryCount + 1}/${maxRetries})...`);
            setTimeout(
              () =>
                loadDeBridgeScript(retryCount + 1, maxRetries)
                  .then(resolve)
                  .catch(reject),
              2000
            );
          } else {
            reject(new Error('Failed to load deBridge widget script after retries'));
          }
        };
        document.body.appendChild(script);
      });
    };

    const getWidgetDimensions = () => {
      const mount = document.getElementById('BESC-CrossPort');
      const container = mount?.parentElement;
      const mountWidth = mount?.clientWidth || container?.clientWidth;
      const mountHeight = mount?.clientHeight || container?.clientHeight;
      const heightBuffer = window.innerWidth < 640 ? 80 : 120;
      const minHeight = window.innerWidth < 640 ? 620 : 660;
      const heightLimit = 900;
      const fallbackWidth =
        window.innerWidth < 640 ? window.innerWidth - 24 : Math.min(window.innerWidth - 160, 880);
      const width = Math.max(280, mountWidth || fallbackWidth);
      const baseHeight = window.innerHeight - heightBuffer;
      let height = Math.min(Math.max(baseHeight, minHeight), heightLimit);

      if (mountHeight) {
        height = Math.max(height, mountHeight);
      }

      return { width, height };
    };

    const initWidget = async () => {
      setScriptError(null);
      setIsWidgetReady(false);

      try {
        await loadDeBridgeScript();
        console.log('DeBridge script loaded:', window.deBridge);

        if (window.deBridge) {
          // 🚀 Ensure the mount div is clean before rendering widget
          const mount = document.getElementById('BESC-CrossPort');
          if (mount) mount.innerHTML = '';

          const { width, height } = getWidgetDimensions();

          widgetInstance = await window.deBridge.widget({
            v: '1',
            element: 'BESC-CrossPort',
            title: 'BESC CrossPort',
            description: 'Seamless cross-chain transfers. Any asset. Any chain. Instantly.',
            width,
            height,
            r: '32000',
            affiliateFeePercent: '1',
            affiliateFeeRecipient: '0x717d58c3e05ADe613fBC3ADc1da2e854Fe119547',
            supportedChains: {
              inputChains: {
                '1': 'all',
                '10': 'all',
                '56': 'all',
                '100': 'all',
                '137': 'all',
                '146': 'all',
                '250': 'all',
                '388': 'all',
                '747': 'all',
                '998': 'all',
                '1088': 'all',
                '1514': 'all',
                '2741': 'all',
                '4158': 'all',
                '5000': 'all',
                '7171': 'all',
                '8453': 'all',
                '32769': 'all',
                '42161': 'all',
                '43114': 'all',
                '48900': 'all',
                '59144': 'all',
                '60808': 'all',
                '80094': 'all',
                '98866': 'all',
                '7565164': 'all',
                '245022934': 'all',
              },
              outputChains: {
                '1': 'all',
                '10': 'all',
                '56': 'all',
                '100': 'all',
                '137': 'all',
                '146': 'all',
                '250': 'all',
                '388': 'all',
                '747': 'all',
                '998': 'all',
                '999': 'all',
                '1088': 'all',
                '1514': 'all',
                '2741': 'all',
                '4158': 'all',
                '5000': 'all',
                '7171': 'all',
                '8453': 'all',
                '32769': 'all',
                '42161': 'all',
                '43114': 'all',
                '48900': 'all',
                '59144': 'all',
                '60808': 'all',
                '80094': 'all',
                '98866': 'all',
                '7565164': 'all',
                '245022934': 'all',
              },
            },
            inputChain: 1,
            outputChain: 1,
            inputCurrency: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
            outputCurrency: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
            address: '',
            showSwapTransfer: true,
            amount: '',
            outputAmount: '',
            isAmountFromNotModifiable: false,
            isAmountToNotModifiable: false,
            lang: 'en',
            mode: 'deswap',
            isEnableCalldata: false,
            styles: btoa(
              JSON.stringify({
                appBackground: '#0e0e1a',
                appAccentBg: '#191927',
                chartBg: '#1e1e2e',
                primary: '#ab58ff',
                secondary: '#ab58ffcc',
                badge: '#292944',
                borderColor: '#2c2c3c',
                borderRadius: 14,
                fontColor: '#ffffff',
                fontColorAccent: '#ab58ff',
                fontFamily: 'Inter, sans-serif',
                btnFontWeight: 500,
              })
            ),
            theme: 'dark',
            isHideLogo: true,
            logo: '',
            disabledWallets: [],
            disabledElements: ['Points', 'Routing', 'Latest trades', 'Exchange rate'],
          });

          console.log('Widget initialized:', widgetInstance);
          readyTimeout = setTimeout(() => {
            setIsWidgetReady(true);
          }, 200);
          widgetInstance.on('order', (widget, params) => {
            console.log('BESC CrossPort order created:', params.orderId, params.status);
            if (params.url) {
              console.log('Blocking external navigation to:', params.url);
            }
          });
          widgetInstance.on('bridge', (widget, params) => {
            console.log('BESC CrossPort bridge completed:', params.status);
          });
          widgetInstance.on('error', (error) => {
            console.error('BESC CrossPort error:', error);
            setScriptError('Error bridging assets: ' + (error.message || 'Unknown error'));
          });

          const widgetElement = document.getElementById('BESC-CrossPort');
          if (widgetElement) {
            widgetElement.focus();
          }
        } else {
          setScriptError('DeBridge widget script not loaded');
        }
      } catch (error) {
        console.error('Widget initialization failed:', error);
        setScriptError('Failed to initialize BESC CrossPort: ' + (error.message || 'Unknown error'));
      }
    };

    initWidget();

    // Cleanup on unmount
    return () => {
      if (cleanedRef.current) return;
      cleanedRef.current = true;

      if (readyTimeout) {
        clearTimeout(readyTimeout);
      }

      try {
        widgetInstance?.destroy?.(); // official destroy if available
      } catch {}

      const el = document.getElementById('BESC-CrossPort');
      if (el) el.innerHTML = ''; // ensure container is empty
    };
  }, []);

  return (
    <section className="crossport-page">
      <div className="crossport-stack" ref={widgetRef} tabIndex={-1}>
        {scriptError && <p className="crossport-error">{scriptError}</p>}

        <div className="crossport-widget" aria-live="polite">
          {!isWidgetReady && !scriptError && (
            <div className="crossport-widget__cover">
              <p>Loading CrossPort widget...</p>
            </div>
          )}
          <div id="BESC-CrossPort" className="crossport-widget__mount"></div>
        </div>
      </div>
    </section>
  );
}

export default CrossPort;
